<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <!-- Data Table -->
        <event-management-table
          :items="allStallHolders"
          :icon="tableIcon"
          :title="tableTitle"
          :buttons="tableButtons"
          :headers="tableHeaders"
          :no-data-message="noDataMessage"
          :show-actions="canManageStallholders"
          @create="openCreateDialog"
          @edit="editItem"
          @delete="deleteItem"
        />

        <!-- Stallholder Dialog (v-if avoids inherit checkboxes flashing true
             when editedItem resets to defaults during close animation) -->
        <stallholder-dialog
          v-if="dialog"
          v-model="dialog"
          :stallholder="editedItem"
          :is-editing="editedIndex !== -1"
          :selected-event="selectedEvent"
          :printing-list="PrintingList"
          :enforce-types="EnforceTypes"
          @save="handleSave"
          @cancel="close"
        />

        <!-- Delete Confirmation Dialog -->
        <delete-confirmation-dialog
          v-model="dialogDelete"
          title="Are you sure you want to delete Stallholder?"
          @confirm="deleteItemConfirm"
          @cancel="closeDelete"
        />
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import "izitoast/dist/css/iziToast.min.css"; // loading css
import iziToast from "izitoast/dist/js/iziToast.min.js"; // you have access to iziToast now
import { mapState, mapActions, mapMutations } from "vuex";
import EventManagementTable from "@/components/tables/EventManagementTable.vue";
import StallholderDialog from "@/components/dialogs/StallholderDialog.vue";
import DeleteConfirmationDialog from "@/components/dialogs/DeleteConfirmationDialog.vue";

export default {
  name: "Stallholders",
  components: {
    EventManagementTable,
    StallholderDialog,
    DeleteConfirmationDialog,
  },
  data: () => ({
    dialog: false,
    dialogDelete: false,
    editedIndex: -1,

    // Table configuration
    tableIcon: "mdi-home",
    tableTitle: "Stallholders",
    tableHeaders: [
      { text: "Stallholder Name", value: "StallholderName" },
      { text: "ABN", value: "Abn" },
      { text: "Card Surcharge (%)", value: "CardSurchargePercentage" },
      { text: "Payment Profile", value: "PaymentProfile" },
      { text: "Printing Profile", value: "PrintingProfile" },
      { text: "Standard Drink Limit", value: "StandardDrinkLimit" },
      { text: "Drink Limit Profile", value: "StandardDrinkLimitEnforce" },
      { text: "Actions", value: "actions", sortable: false },
    ],
    noDataMessage: "No stallholders for this event",

    PrintingList: [
      { id: 1, name: "Print" },
      { id: 2, name: "Prompt" },
      { id: 3, name: "Bypass" },
    ],

    EnforceTypes: [
      { id: true, name: "Enforce" },
      { id: false, name: "Prompt" },
    ],

    editedItem: {
      EventId: "",
      StallholderName: "",
      Abn: "",
      PaymentProfile: [],
      PrintingProfile: "",
      StandardDrinkLimit: "",
      StandardDrinkLimitEnforce: "",
      CardSurchargePercentage: "",
      PaymentProfileInherit: true,
      PrintingProfileInherit: true,
      StandardDrinkLimitInherit: true,
      StandardDrinkLimitEnforceInherit: true,
      CardSurchargePercentageInherit: true,
    },
    defaultItem: {
      EventId: "",
      StallholderName: "",
      Abn: "",
      PaymentProfile: [],
      PrintingProfile: "",
      StandardDrinkLimit: "",
      StandardDrinkLimitEnforce: "",
      CardSurchargePercentage: "",
      PaymentProfileInherit: true,
      PrintingProfileInherit: true,
      StandardDrinkLimitInherit: true,
      StandardDrinkLimitEnforceInherit: true,
      CardSurchargePercentageInherit: true,
    },
  }),

  updated: function () {
    console.log("BOO", this.selectedEvent);
  },

  computed: {
    ...mapState("filter", ["globalEventFilter", "allStallHolders", "IsAdmin"]),

    selectedEvent() {
      return this.globalEventFilter;
    },

    eventPermission() {
      return this.globalEventFilter?.Permission || "None";
    },

    canManageStallholders() {
      return this.IsAdmin || this.eventPermission === "Write";
    },

    tableButtons() {
      if (!this.canManageStallholders) return [];
      return [
        {
          name: "Add StallHolder",
          icon: "mdi-plus",
          function: "create",
        },
      ];
    },
  },

  watch: {
    dialog(val) {
      if (val === true) {
        this.setEventSelectorDisabled(true);
      } else {
        this.setEventSelectorDisabled(false);
      }
    },
    dialogDelete(val) {
      if (val === true) {
        this.setEventSelectorDisabled(true);
      } else {
        this.setEventSelectorDisabled(false);
      }
    },
  },

  methods: {
    ...mapMutations("filter", ["setFilterStallHolders"]),
    ...mapMutations("admin", [
      "setEventSelectorDisabled",
      "setCurrentStallholder",
    ]),
    ...mapActions("admin", ["createEvent", "getEventMetadata"]),
    ...mapActions("filter", ["initialLogin"]),

    openCreateDialog() {
      if (!this.canManageStallholders) return;
      this.editedIndex = -1;
      this.editedItem = { ...this.defaultItem };
      this.dialog = true;
    },

    editItem(item) {
      if (!this.canManageStallholders) return;
      this.editedIndex = this.allStallHolders.indexOf(item);
      this.editedItem = { ...item };
      this.dialog = true;
    },

    deleteItem(item) {
      if (!this.canManageStallholders) return;
      this.editedIndex = this.allStallHolders.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.dialogDelete = true;
    },

    deleteItemConfirm() {
      this.allStallHolders.splice(this.editedIndex, 1);

      try {
        if (this.editedItem.StallholderId) {
          let event_item = [
            {
              Type: "Stallholder",
              Action: "delete",
              Properties: this.editedItem,
            },
          ];

          let submitEvent = {
            event_items: event_item,
          };
          this.createEvent(submitEvent)
            .then(async (response) => {
              console.log("iziToast response", response);

              // Refresh the event metadata to update admin state after deletion
              try {
                await this.getEventMetadata(this.selectedEvent.EventId);
                console.log(
                  "Event metadata refreshed after deleting stallholder"
                );
              } catch (error) {
                console.error("Failed to refresh event metadata:", error);
              }

              iziToast.success({
                title: "Stallholder",
                message: "Successfully deleted the record!",
                position: "topRight",
              });
            })
            .catch((err) => {
              console.log("err");
              console.log(err);

              iziToast.warning({
                title: "Stallholder",
                message: "Fail to delete the record!",
                position: "topRight",
              });
            });
        }
      } catch (e) {
        console.log(e);
      }

      this.closeDelete();
    },

    handleSave(stallholderData) {
      console.log("Starting handleSave with data:", stallholderData);

      // Update the local data
      if (this.editedIndex > -1) {
        Object.assign(this.allStallHolders[this.editedIndex], stallholderData);
      } else {
        this.allStallHolders.push(stallholderData);
      }

      try {
        // Create a copy of stallholderData and remove unwanted keys
        const cleanedStallholderData = { ...stallholderData };
        delete cleanedStallholderData.Stalls;
        delete cleanedStallholderData.TerminalUsers;
        delete cleanedStallholderData.isAdmin;

        console.log("Sent event item: ", cleanedStallholderData);
        let event_item = [
          {
            Type: "Stallholder",
            Action: this.editedIndex === -1 ? "create" : "update",
            Properties: cleanedStallholderData,
          },
        ];

        let submitEvent = {
          event_items: event_item,
        };

        this.createEvent(submitEvent)
          .then(async (response) => {
            console.log(response);

            // Refresh the event metadata to update admin state after create/update
            try {
              await this.getEventMetadata(this.selectedEvent.EventId);
              console.log(
                `Event metadata refreshed after ${
                  this.editedIndex === -1 ? "creating" : "updating"
                } stallholder`
              );
            } catch (error) {
              console.error("Failed to refresh event metadata:", error);
            }

            // If creating a new stallholder, set it as the current stallholder
            if (this.editedIndex === -1) {
              // Find the newly created stallholder in the updated list
              const newStallholder = this.allStallHolders.find(
                (sh) =>
                  sh.StallholderName === stallholderData.StallholderName &&
                  sh.Abn === stallholderData.Abn
              );
              if (newStallholder) {
                this.setCurrentStallholder(newStallholder);
                console.log("Set new stallholder as current:", newStallholder);
              }
            }

            iziToast.success({
              title: "Stallholder",
              message: "Successfully inserted record!",
              position: "topRight",
            });

            this.redirectReload();
          })
          .catch((err) => {
            console.log("err");
            console.log(err);

            iziToast.warning({
              title: "Stallholder",
              message: "Fail to inserted record!",
              position: "topRight",
            });
          });
      } catch (e) {
        console.log(e);
      }
      this.close();
    },

    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },

    closeDelete() {
      this.dialogDelete = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },
    redirectReload() {
      this.initialLogin().then((response) => {
        console.log(response);
      });
    },
  },
};
</script>
<style scoped>
h1 {
  color: #a09c94;
  font-family: sans-serif;
}
</style>
